import { isOneOf } from '@foundation-player/utils';

/**
 * The type of content you want to play.
 *
 * @remarks
 * The player can automatically distinguish between on-demand content and live streams. However,
 * this distinction is only made at the technical level.
 *
 * In the context of RTL, we distinguish between different types at the business level. Since
 * these types only differ at the business level but not at the technical level, we cannot
 * recognize this automatically. However, we need this differentiation so that the player and its
 * components can behave accordingly.
 *
 * This type reflects the business level of the stream. PlayerInterface.isLive is the
 * technical level.
 */
export const SourceType = {
  /**
   * Regular video on demand content with a fixed duration.
   *
   * @remarks
   * This is the default type of stream.
   * This is also used for non-video content like radio streams, podcasts and audiobooks.
   *
   * The player is not really concerned weather the content is a video or not,
   * it just needs to know if it has a fixed duration or not.
   *
   * In the future we might want to introduce a new type for non-video content,
   * so components can behave differently for audio content.
   */
  VOD: 'vod',

  /**
   * A livestream which is running 24/7
   *
   * @remarks
   * Technically this is the same as a regular livestream that is running for an indefinite amount of time.
   *
   * In the context of RTL this is used for normal TV channels.
   * In this context the player has to behave differently than for a event livestream.
   * Specifically we may have to cap the bitrate for certain devices, because the streams are different and send other analytics data.
   *
   * Because the stream is running an indefinite amount of time, some features like post-roll ads are not possible.
   *
   * Client may also want to display a different UI for TV channels and update the current content according to the EPG data.
   * For those use cases we support changing the title and description of the stream via our analytics API.
   * You can also use the PlayerInterface.setMetadata api to update the metadata.
   */
  Live: 'live',

  /**
   * A livestreams with a scheduled start and end date.
   *
   * @remarks
   * Technically this is the same as a regular livestream on other streaming platforms.
   *
   * In the context of RTL this is used for special events like football games, music events, etc.
   * In this context the player has to behave differently than for a live-tv livestream.
   * Specifically we may have to cap the bitrate for certain devices, because the streams are different and send other analytics data.
   *
   * The most important different for the client is that the player can also play post-roll ads for event livestreams and emit events when the content and session ends.
   */
  Event: 'event',

  /**
   * A livestream that is running 24/7 with advertising.
   *
   * @remarks
   * FAST is the acronym for "Free Ad-Supported Streaming TV". Technically we handle it like an event stream with server side ad insertion (ssai) enabled.
   */
  FAST: 'fast',

  /**
   * A podcast episode.
   */
  Podcast: 'podcast',

  /**
   * An audio book chapter.
   */
  Audiobook: 'audiobook',

  /**
   * A radio live stream that is running 24/7.
   */
  Radio: 'radio',

  /**
   * A Text To Speech stream.
   */
  TTS: 'tts',
} as const;

/**
 * Typeguard to check if the given data is of the type `SourceType`
 */
export const isSourceType = (data: unknown): data is SourceType => isOneOf(data, Object.values(SourceType));

/**
 * {@inheritDoc SourceType:var}
 */
export type SourceType = (typeof SourceType)[keyof typeof SourceType];
